// War-map lookups the event bindings need, built once from the match setup: cities by SimId, strike origins and the
// immutable oriented route references incident to each city, so ownership sweeps reveal along the real land roads
// (water has no connections, taxgw-lcaj). Both orientations share one static geometry per physical edge; nothing is
// cloned per effect or per play.

import { InvariantViolation, NO_SIM_ID, type CityIndex, type MapCityView, type MatchSetupView, type PlayerId, type SimId } from '@taxwar/net'
import { capitalOf, seatsOf } from '../match/warSetup'
import type { ReadyRoute } from './placement'
import { RouteGeometryCache } from './routeGeometry'

export class WarGeography {
  private readonly bySimId = new Map<number, MapCityView>()
  /** Per seat. */
  private readonly capitals: readonly MapCityView[]
  /** Ready oriented route references for every incident physical setup edge, per city. */
  private readonly incidentRoutes: readonly (readonly ReadyRoute[])[]
  private readonly routes: RouteGeometryCache

  constructor(readonly setup: MatchSetupView) {
    for (const city of setup.cities) this.bySimId.set(city.simId, city)
    this.capitals = seatsOf(setup).map((seat) => capitalOf(setup, seat))
    // One static geometry per physical edge, built once for the whole war; the two orientations only differ by direction.
    this.routes = new RouteGeometryCache(setup.edges.map((edge) => edge.path))
    const incident: ReadyRoute[][] = setup.cities.map(() => [])
    let index = 0
    for (const edge of setup.edges) {
      const geometry = this.routes.geometryOf(index)
      const forward: ReadyRoute = { kind: 'route', geometry, direction: 1 }
      const reverse: ReadyRoute = { kind: 'route', geometry, direction: -1 }
      this.routeList(incident, edge.a).push(Object.freeze(forward))
      this.routeList(incident, edge.b).push(Object.freeze(reverse))
      index++
    }
    this.incidentRoutes = incident
  }

  city(simId: SimId): MapCityView {
    const city = this.bySimId.get(simId)
    if (city === undefined) throw new InvariantViolation(`event names city ${simId}, which is not on the war map`)
    return city
  }

  /**
   * Where a strike leaves from: a missile's launch city (its launch doctrine's pick, GDD §7.1), or the issuing
   * capital for immediate strikes, which have no launch city (`site` NO_SIM_ID).
   */
  strikeOrigin(owner: PlayerId, site: SimId): MapCityView {
    if (site !== NO_SIM_ID) return this.city(site)
    const capital = this.capitals[owner]
    if (capital === undefined) throw new InvariantViolation(`strike owner ${owner} is not a belligerent of this war`)
    return capital
  }

  /** The ready route references leaving `index`, one per incident physical edge. */
  incidentEdges(index: CityIndex): readonly ReadyRoute[] {
    const list = this.incidentRoutes[index]
    if (list === undefined) throw new InvariantViolation(`war map has no city at index ${index}`)
    return list
  }

  /** Releases the shared route geometries (once, not per mesh); the ready references into them die with the match. */
  dispose(): void {
    this.routes.dispose()
  }

  private routeList(incident: ReadyRoute[][], index: CityIndex): ReadyRoute[] {
    const list = incident[index]
    if (list === undefined) throw new InvariantViolation(`war map edge names city index ${index}, which does not exist`)
    return list
  }
}
